import { io } from 'socket.io-client';
import { HOST } from '@api/constant';
import getId from '../../api/getId';
import { socketAuth } from '@utils/socketAuthToken';

// Connection state types (for documentation)
// 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING' | 'ERROR'

/**
 * Unified SocketManager - Single connection with namespace multiplexing
 * All namespaces share one WebSocket connection for better efficiency
 */
class UnifiedSocketManager {
  static instance = null;
  baseSocket = null;
  namespaceSockets = new Map();
  connectionState = 'DISCONNECTED';
  connectionPromise = null;
  reconnectAttempts = 0;
  maxReconnectAttempts = Infinity;
  reconnectDelay = 1000;
  maxReconnectDelay = 30000;
  shouldReconnect = true;
  heartbeatInterval = null;
  reconnectTimer = null; // Single pending reconnect timeout (never stack)
  connectionTimeout = null;
  operationQueue = [];
  eventListeners = new Map(); // namespace -> event -> callbacks
  connectionStateCallbacks = new Set();
  lastHeartbeat = Date.now();
  heartbeatTimeout = 60000;
  pendingJoins = new Map(); // namespace -> user data
  joinedNamespaces = new Map(); // namespace -> user data (track successfully joined namespaces)
  lastSocketId = null; // Track socket ID to detect reconnections
  _initializing = false; // Guard against concurrent initializeBaseSocket
  _lifecycleHooksInstalled = false;
  _pongHandler = null;

  constructor() {
    // Private constructor for singleton pattern
    if (UnifiedSocketManager.instance) {
      return UnifiedSocketManager.instance;
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!UnifiedSocketManager.instance) {
      UnifiedSocketManager.instance = new UnifiedSocketManager();
      UnifiedSocketManager.instance.installLifecycleHooks();
    }
    return UnifiedSocketManager.instance;
  }

  /**
   * Reconnect when the tab becomes visible or the browser comes back online.
   * Installed once per singleton.
   */
  installLifecycleHooks() {
    if (this._lifecycleHooksInstalled || typeof window === 'undefined') {
      return;
    }
    this._lifecycleHooksInstalled = true;

    const tryResume = () => {
      if (!this.shouldReconnect) return;
      if (this.isConnected()) {
        this.lastHeartbeat = Date.now();
        return;
      }
      // Drop stale in-flight promise so connect() can run again
      this.connectionPromise = null;
      this.connect().catch((err) => {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn('[UnifiedSocketManager] Lifecycle resume connect failed:', err?.message || err);
        }
      });
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          tryResume();
        }
      });
    }
    window.addEventListener('online', tryResume);
  }

  /**
   * Initialize base socket connection
   */
  initializeBaseSocket(forceNew = false) {
    // Prevent concurrent initialization (e.g. multiple connect() callers in same tick)
    if (this._initializing) return;
    // Don't create a new socket if one already exists and is connected or connecting
    if (!forceNew && this.baseSocket && (this.baseSocket.connected || this.baseSocket.connecting)) {
      return;
    }

    this._initializing = true;
    try {
    // Clean up old socket if it exists but is disconnected, or if forceNew is true
    if (this.baseSocket && (!this.baseSocket.connected || forceNew)) {
      this.baseSocket.removeAllListeners();
      this.baseSocket.disconnect();
      this.baseSocket = null;
    }

    // Always get fresh userId on initialization (important for reconnections after server/frontend restarts)
    const userId = getId();
    const queryParams = userId ? { userId: String(userId) } : {};

    // Create single base connection (no namespace in URL)
    // Use forceNew: true when reconnecting to ensure fresh connection with updated query/auth params
    // Use forceNew: false on initial connection to allow Socket.IO to reuse existing connections
    this.baseSocket = io.connect(HOST, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      autoconnect: false,
      query: queryParams,
      reconnection: false, // We handle reconnection manually
      reconnectionDelay: 0,
      reconnectionAttempts: 0,
      timeout: 20000,
      forceNew: forceNew, // Use forceNew when reconnecting to ensure fresh connection with updated params
      auth: socketAuth(userId),
    });

    this.setupBaseSocketHandlers();
    } finally {
      this._initializing = false;
    }
  }

  /**
   * Setup base socket event handlers
   */
  setupBaseSocketHandlers() {
    if (!this.baseSocket) return;

    // Connection successful
    this.baseSocket.on('connect', () => {
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      // Check if socket ID changed (reconnection with new socket)
      const socketIdChanged = this.lastSocketId !== null && this.lastSocketId !== this.baseSocket?.id;

      // Save previously joined namespaces BEFORE clearing (for reconnection)
      // This ensures we can rejoin all namespaces even if they're not in pendingJoins
      const previouslyJoinedNamespaces = socketIdChanged ? new Map(this.joinedNamespaces) : null;

      if (socketIdChanged) {
        // Clear joinedNamespaces so we force rejoin even if frontend thinks they're already joined
        // The backend has a new socket with empty joinedNamespaces Set
        this.joinedNamespaces.clear();
      }
      this.lastSocketId = this.baseSocket?.id;

      this.setConnectionState('CONNECTED');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.connectionPromise = null;
      this.clearReconnectTimer();

      // Start heartbeat
      this.startHeartbeat();

      // Process queued operations
      this.processOperationQueue();

      // Rejoin all previously joined namespaces first
      // If socket ID changed, merge previouslyJoinedNamespaces with pendingJoins to ensure all namespaces rejoin
      // Otherwise, rejoin from joinedNamespaces
      let namespacesToRejoin;
      if (socketIdChanged) {
        // Merge previously joined namespaces with pending joins
        // This ensures we rejoin ALL namespaces that were previously joined, not just pending ones
        namespacesToRejoin = new Map(previouslyJoinedNamespaces);
        // Add any pending joins that aren't already in the list
        this.pendingJoins.forEach((userData, namespace) => {
          if (!namespacesToRejoin.has(namespace)) {
            namespacesToRejoin.set(namespace, userData);
          }
        });
      } else {
        namespacesToRejoin = this.joinedNamespaces;
      }

      namespacesToRejoin.forEach((userData, namespace) => {
        this.joinNamespace(namespace, userData, true); // Force rejoin even if alreadyJoined
      });
      
      // Then join any new pending namespaces that weren't in the rejoin list
      this.pendingJoins.forEach((userData, namespace) => {
        if (!this.joinedNamespaces.has(namespace)) {
          this.joinNamespace(namespace, userData);
        }
      });
      this.pendingJoins.clear();

      // Reregister all event listeners
      this.reregisterEventListeners();
    });

    // Disconnection
    this.baseSocket.on('disconnect', (reason) => {
      this.setConnectionState('DISCONNECTED');
      this.stopHeartbeat();

      // Clear namespace sockets (but keep joinedNamespaces and eventListeners for reconnection)
      // When socket reconnects, components will get fresh proxies via getNamespaceSocket()
      // The eventListeners map is preserved so existing listeners can still be called
      // NOTE: We clear namespaceSockets so components get fresh proxies, but eventListeners
      // are preserved so registered listeners still work
      this.namespaceSockets.clear();
      // Don't clear joinedNamespaces - we need them to rejoin on reconnect
      // Don't clear eventListeners - they're still valid and will work with new base socket

      // Don't reconnect if manually disconnected
      if (reason === 'io client disconnect') {
        this.shouldReconnect = false;
        return;
      }

      // Clear the connection promise so a new one can be created
      this.connectionPromise = null;

      // Attempt reconnection
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    });

    // Connection error
    this.baseSocket.on('connect_error', (error) => {
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      // Server rejected because another socket for this user is still marked active.
      // Never fake CONNECTED or permanently disable reconnect — drop this attempt and retry once.
      const errMsg = error?.message || '';
      if (errMsg === 'DUPLICATE_CONNECTION' || errMsg.includes('DUPLICATE_CONNECTION')) {
        if (this.baseSocket) {
          try {
            this.baseSocket.removeAllListeners();
            this.baseSocket.disconnect();
          } catch {
            /* ignore */
          }
          this.baseSocket = null;
        }
        this.connectionPromise = null;
        this.setConnectionState('DISCONNECTED');
        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
        return;
      }

      this.setConnectionState('ERROR');

      if (this.connectionPromise) {
        this.connectionPromise = null;
      }

      // Schedule reconnection if not manually disconnected
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    });

    // Socket error
    this.baseSocket.on('error', (error) => {
        this.setConnectionState('ERROR');
    });

    // Listen for namespace-specific events
    // Events are prefixed with namespace:eventName
    this.baseSocket.onAny((eventName, ...args) => {
      // Check if event is namespace-prefixed (format: namespace:eventName)
      const parts = eventName.split(':');
      if (parts.length >= 2) {
        let namespace = parts[0];
        let actualEvent = parts.slice(1).join(':');
        
        // Handle duplicate namespace prefixes (e.g., "notifications:notifications:notification")
        // If the actualEvent starts with the same namespace, remove the duplicate
        if (actualEvent.startsWith(namespace + ':')) {
          console.warn(`⚠️ [UnifiedSocketManager] Detected duplicate namespace prefix in event: ${eventName}, correcting to ${namespace}:${actualEvent.substring(namespace.length + 1)}`);
          actualEvent = actualEvent.substring(namespace.length + 1);
        }
        
        // Handle namespace join confirmations
        if (actualEvent === 'joined') {
          const data = args[0] || {};
          if (data.success) {
            // Track this namespace as successfully joined
            // Get userData from pendingJoins or use existing
            const userData = this.pendingJoins.get(namespace) || this.joinedNamespaces.get(namespace) || data.userData;
            if (userData || namespace) {
              this.joinedNamespaces.set(namespace, userData || {});
            }
          }
          return; // Don't route join confirmations to event listeners
        }
        
        
        // Route to namespace-specific listeners
        const namespaceListeners = this.eventListeners.get(namespace);
        if (namespaceListeners) {
          const eventListeners = namespaceListeners.get(actualEvent);
          if (eventListeners && eventListeners.size > 0) {
            eventListeners.forEach((callback, index) => {
              try {
                callback(...args);
              } catch (error) {
                console.error(`❌ Error in ${namespace}:${actualEvent} listener ${index + 1}:`, error);
                console.error(`❌ Error stack:`, error.stack);
              }
            });
          } else {
            // Backward compatibility: backend may emit notifications:event; app listens for 'event', 'notification', or 'data_notification'.
            let routedViaCompatibility = false;
            if (namespace === 'notifications' && actualEvent === 'event') {
              const compatibilityEvents = ['event', 'notification', 'data_notification'];
              for (const fallbackEvent of compatibilityEvents) {
                const fallbackListeners = namespaceListeners.get(fallbackEvent);
                if (fallbackListeners && fallbackListeners.size > 0) {
                  fallbackListeners.forEach((callback) => {
                    try {
                      callback(...args);
                    } catch (error) {
                      console.error(`❌ Error in ${namespace}:${fallbackEvent} compatibility listener:`, error);
                    }
                  });
                  routedViaCompatibility = true;
                  break;
                }
              }
              // Known backend emission; no error log if no listener (e.g. screen not mounted yet).
              if (!routedViaCompatibility) return;
            }

            if (!routedViaCompatibility) {
              console.error(`❌ [UnifiedSocketManager] No listeners registered for ${namespace}:${actualEvent}`, {
              namespace,
              actualEvent,
              availableEvents: Array.from(namespaceListeners.keys()),
              allNamespaces: Array.from(this.eventListeners.keys()),
              joinedNamespaces: Array.from(this.joinedNamespaces.keys()),
              namespaceListenersSize: namespaceListeners.size
            });
            }
          }
        } else {
          console.error(`❌ [UnifiedSocketManager] No listeners registered for namespace: ${namespace}`, {
            namespace,
            availableNamespaces: Array.from(this.eventListeners.keys()),
            joinedNamespaces: Array.from(this.joinedNamespaces.keys()),
            eventListenersSize: this.eventListeners.size
          });
        }
      } else {
        // Handle non-namespaced events (fallback)
        // Check if event name matches a joined namespace (for backward compatibility)
        // Some backends might emit events directly with namespace name as event name
        let routed = false;
        for (const [namespace] of this.joinedNamespaces) {
          if (eventName === namespace || eventName.startsWith(namespace)) {
            // Try to route to this namespace
            const namespaceListeners = this.eventListeners.get(namespace);
            if (namespaceListeners) {
              // Try common event names (order matters - try most specific first)
              const commonEvents = ['notification', 'notifications', 'event', 'data_notification', 'test_event', 'billing_entitlements_refresh'];
              for (const commonEvent of commonEvents) {
                const eventListeners = namespaceListeners.get(commonEvent);
                if (eventListeners && eventListeners.size > 0) {
                  eventListeners.forEach(callback => {
                    try {
                      callback(...args);
                    } catch (error) {
                      console.error(`❌ Error in ${namespace}:${commonEvent} listener:`, error);
                    }
                  });
                  routed = true;
                  break;
                }
              }
              // If no common event matched, try the event name itself
              if (!routed) {
                const eventListeners = namespaceListeners.get(eventName);
                if (eventListeners && eventListeners.size > 0) {
                  eventListeners.forEach(callback => {
                    try {
                      callback(...args);
                    } catch (error) {
                      console.error(`❌ Error in ${namespace}:${eventName} listener:`, error);
                    }
                  });
                  routed = true;
                } else {
                  console.warn(`⚠️ [UnifiedSocketManager] No listeners found for '${namespace}:${eventName}' or any common event names`);
                }
              }
            } else {
              console.warn(`⚠️ [UnifiedSocketManager] No namespace listeners map found for namespace '${namespace}'`);
            }
            if (routed) break;
          }
        }
      }
    });
  }

  /**
   * Join a namespace (server-side)
   * @param {string} namespace - Namespace to join
   * @param {object} userData - Optional user data
   * @param {boolean} forceRejoin - Force rejoin even if already marked as joined (for reconnections)
   */
  joinNamespace(namespace, userData, forceRejoin = false) {

    if (!this.baseSocket?.connected) {
      // Queue for later
      if (userData) {
        this.pendingJoins.set(namespace, userData);
      } else if (!this.pendingJoins.has(namespace)) {
        this.pendingJoins.set(namespace, null);
      }
      return;
    }

    // Prevent duplicate namespace joins (unless forceRejoin is true, which happens after socket ID changes)
    if (this.joinedNamespaces.has(namespace) && !forceRejoin) {
      return;
    }

    // Emit join event with namespace info
    this.baseSocket.emit('join_namespace', { namespace, userData });
    // Mark as joined immediately to prevent duplicate joins
    if (userData) {
      this.joinedNamespaces.set(namespace, userData);
    } else {
      // Even without userData, mark as joined to track state
      this.joinedNamespaces.set(namespace, null);
    }
  }

  /**
   * Leave a logical namespace without tearing down the shared base socket or listeners.
   */
  leaveNamespace(namespace) {
    if (this.baseSocket?.connected) {
      try {
        this.baseSocket.emit('leave_namespace', { namespace });
      } catch {
        /* ignore */
      }
    }
    this.joinedNamespaces.delete(namespace);
    this.pendingJoins.delete(namespace);
    // Keep namespaceSockets proxy + eventListeners so remounts / other consumers keep working
  }

  /**
   * Get or create namespace socket (proxy object)
   */
  getNamespaceSocket(namespace, userData) {
    // Always check if we need to recreate the proxy (e.g., after reconnection)
    // If baseSocket changed, we should recreate proxies to ensure they reference the current socket
    const existingProxy = this.namespaceSockets.get(namespace);
    const currentBaseSocketId = this.baseSocket?.id;
    const needsNewProxy = !existingProxy || 
                         (currentBaseSocketId && existingProxy && existingProxy._baseSocketId !== currentBaseSocketId);
    
    if (existingProxy && !needsNewProxy) {
      // Proxy exists and is still valid, return it
      // Update user data if provided, but only join if not already joined
      if (userData && !this.joinedNamespaces.has(namespace)) {
        this.pendingJoins.set(namespace, userData);
        if (this.baseSocket?.connected) {
          this.joinNamespace(namespace, userData);
        }
      }
      return existingProxy;
    }

    // Create new namespace socket proxy (either doesn't exist or baseSocket changed)
    if (needsNewProxy && existingProxy) {
    }
    const namespaceSocket = this.createNamespaceSocketProxy(namespace);
    // Store baseSocket ID on proxy for comparison
    if (this.baseSocket) {
      namespaceSocket._baseSocketId = this.baseSocket.id;
    }
    // Add a marker to identify this as a proxy
    namespaceSocket._isProxy = true;
    namespaceSocket._namespace = namespace;
    this.namespaceSockets.set(namespace, namespaceSocket);

    // Join namespace if connected and not already joined
    if (userData && !this.joinedNamespaces.has(namespace)) {
      this.pendingJoins.set(namespace, userData);
      if (this.baseSocket?.connected) {
        this.joinNamespace(namespace, userData);
      }
    }

    return namespaceSocket;
  }

  /**
   * Create a proxy object that mimics socket.io socket for a namespace
   */
  createNamespaceSocketProxy(namespace) {
    const self = this;
    
    return {
      namespace,
      get id() {
        return self.baseSocket?.id;
      },
      get connected() {
        return self.baseSocket?.connected === true;
      },
      get nsp() {
        return {
          name: `/${namespace}`,
        };
      },
      get io() {
        return self.baseSocket?.io;
      },
      on(event, callback) {
        if (!callback || typeof callback !== 'function') {
          console.error(`❌ [NamespaceProxy] Invalid callback provided for ${namespace}:${event}`, callback);
          return;
        }
        self.registerEventListener(namespace, event, callback);
      },
      once(event, callback) {
        // Create a wrapper that removes itself after first call
        const onceWrapper = (...args) => {
          self.removeEventListener(namespace, event, onceWrapper);
          callback(...args);
        };
        self.registerEventListener(namespace, event, onceWrapper);
      },
      off(event, callback) {
        if (callback) {
          self.removeEventListener(namespace, event, callback);
        } else {
          self.removeAllEventListeners(namespace, event);
        }
      },
      emit(event, data) {
        self.emitToNamespace(namespace, event, data);
      },
      connect() {
        return self.connect();
      },
      disconnect() {
        // Shared multiplexed connection: leaving a namespace must NOT wipe listeners
        // registered by other consumers (DashStack, ContextTracker, NotificationModal, etc.).
        self.leaveNamespace(namespace);
      },
      removeAllListeners(event) {
        if (event) {
          self.removeAllEventListeners(namespace, event);
        } else {
          self.removeAllEventListeners(namespace);
        }
      },
    };
  }

  /**
   * Emit event to specific namespace
   */
  emitToNamespace(namespace, event, data) {
    if (!this.baseSocket?.connected) {
      // Queue operation
      this.queueOperation(event, data, namespace);
      // Attempt to reconnect
      if (this.shouldReconnect && this.connectionState !== 'CONNECTING' && this.connectionState !== 'RECONNECTING') {
        this.connect().catch(console.error);
      }
      return;
    }

    // Emit with namespace prefix
    const namespacedEvent = `${namespace}:${event}`;
    this.baseSocket.emit(namespacedEvent, data);
  }

  /**
   * Register event listener for namespace
   */
  registerEventListener(namespace, event, callback) {
    
    if (!callback || typeof callback !== 'function') {
      console.error(`❌ [UnifiedSocketManager] Invalid callback provided for ${namespace}:${event}`, callback);
      return;
    }
    
    if (!this.eventListeners.has(namespace)) {
      this.eventListeners.set(namespace, new Map());
    }
    const namespaceListeners = this.eventListeners.get(namespace);
    if (!namespaceListeners) {
      console.error(`❌ [UnifiedSocketManager] Failed to get/create namespace listeners for: ${namespace}`);
      return;
    }
    
    if (!namespaceListeners.has(event)) {
      namespaceListeners.set(event, new Set());
    }
    const eventListeners = namespaceListeners.get(event);
    if (eventListeners) {
      const beforeSize = eventListeners.size;
      // Check if callback already exists (Set will automatically deduplicate, but we want to log it)
      const alreadyExists = eventListeners.has(callback);
      eventListeners.add(callback);
      const afterSize = eventListeners.size;
    } else {
      console.error(`❌ [UnifiedSocketManager] Failed to get/create event listeners for: ${namespace}:${event}`);
    }
  }

  /**
   * Remove event listener for namespace
   */
  removeEventListener(namespace, event, callback) {
    const namespaceListeners = this.eventListeners.get(namespace);
    if (namespaceListeners) {
      const eventListeners = namespaceListeners.get(event);
      if (eventListeners) {
        const beforeSize = eventListeners.size;
        // Check all callbacks before removal
        const callbacksBefore = Array.from(eventListeners).map(cb => ({
          name: cb?.name || 'anonymous',
          preview: cb?.toString?.().substring(0, 80) || 'N/A',
          isMatch: cb === callback
        }));
        
        const removed = eventListeners.delete(callback);
        const afterSize = eventListeners.size;

        if (eventListeners.size === 0) {
          namespaceListeners.delete(event);
        }
      } else {
        console.warn(`🗑️ [UnifiedSocketManager] No event listeners found for ${namespace}:${event} to remove`);
      }
    } else {
      console.warn(`🗑️ [UnifiedSocketManager] No namespace listeners found for ${namespace} to remove from`);
    }
  }

  /**
   * Remove all event listeners for namespace (and optionally specific event)
   */
  removeAllEventListeners(namespace, event) {
    const namespaceListeners = this.eventListeners.get(namespace);
    if (namespaceListeners) {
      if (event) {
        const beforeCount = namespaceListeners.get(event)?.size || 0;
        namespaceListeners.delete(event);
      } else {
        const beforeCount = Array.from(namespaceListeners.values()).reduce((sum, set) => sum + set.size, 0);
        this.eventListeners.delete(namespace);
      }
    }
  }

  /**
   * Reregister all event listeners after reconnection
   */
  reregisterEventListeners() {
    // Event listeners are already registered in memory
    // They will be called when events come through onAny handler
    // No need to re-register with socket.io
  }

  /**
   * Connect base socket.
   * Assigns connectionPromise immediately so concurrent callers share one connection attempt.
   */
  connect() {
    this.shouldReconnect = true;
    // Prefer an immediate connect over a pending backoff timer
    this.clearReconnectTimer();

    // Return existing promise if already connecting (prevents multiple inits from race)
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Return immediately if already connected
    if (this.baseSocket?.connected && this.connectionState === 'CONNECTED') {
      return Promise.resolve(this.baseSocket);
    }

    // Assign promise first, then run init in microtask so concurrent callers see same promise
    this.connectionPromise = new Promise((resolve, reject) => {
      queueMicrotask(() => {
        this._doConnect(resolve, reject);
      });
    });
    return this.connectionPromise;
  }

  /**
   * Internal: perform one connection attempt (called from connect() via microtask).
   */
  _doConnect(resolve, reject) {
    try {
      // Re-check: another microtask might have connected already
      if (this.baseSocket?.connected && this.connectionState === 'CONNECTED') {
        resolve(this.baseSocket);
        return;
      }
      // Initialize socket if needed
      if (!this.baseSocket || (!this.baseSocket.connected && !this.baseSocket.connecting)) {
        if (this.baseSocket && !this.baseSocket.connected) {
          this.baseSocket.removeAllListeners();
          this.baseSocket.disconnect();
          this.baseSocket = null;
        }
        this.initializeBaseSocket(true);
      } else if (this.baseSocket.connecting) {
        // Socket already connecting; wait for it via handlers below
      }

      if (!this.baseSocket) {
        reject(new Error('Failed to create socket'));
        this.connectionPromise = null;
        return;
      }

      this.setConnectionState('CONNECTING');

      this.connectionTimeout = setTimeout(() => {
        if (this.connectionState !== 'CONNECTED') {
          console.error('❌ [UnifiedSocketManager] Connection timeout');
          this.setConnectionState('ERROR');
          this.connectionPromise = null;
          reject(new Error('Connection timeout'));
        }
      }, 20000);

      const connectHandler = () => {
        if (this.baseSocket) {
          this.baseSocket.off('connect', connectHandler);
          this.baseSocket.off('connect_error', errorHandler);
        }
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        resolve(this.baseSocket);
      };

      const errorHandler = (err) => {
        if (this.baseSocket) {
          this.baseSocket.off('connect', connectHandler);
          this.baseSocket.off('connect_error', errorHandler);
        }
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        this.connectionPromise = null;
        reject(err);
      };

      this.baseSocket.on('connect', connectHandler);
      this.baseSocket.on('connect_error', errorHandler);

      if (!this.baseSocket.connected) {
        this.baseSocket.connect();
      }
    } catch (error) {
      console.error('❌ [UnifiedSocketManager] Failed to connect:', error);
      this.setConnectionState('ERROR');
      this.connectionPromise = null;
      reject(error);
    }
  }

  /**
   * Disconnect base socket (logout / full teardown only)
   */
  disconnect() {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.cleanupBaseSocket();
    this.baseSocket = null;
    this.setConnectionState('DISCONNECTED');
    this.operationQueue = [];
    this.connectionPromise = null;
    this.namespaceSockets.clear();
    this.pendingJoins.clear();
    this.joinedNamespaces.clear(); // Only clear on manual disconnect
    this.reconnectAttempts = 0;
  }

  /**
   * Cleanup base socket
   */
  cleanupBaseSocket() {
    this.clearReconnectTimer();
    this.stopHeartbeat();

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.baseSocket) {
      try {
        this.baseSocket.removeAllListeners();
        if (this.baseSocket.connected || this.baseSocket.connecting) {
          this.baseSocket.disconnect();
        }
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Re-enable auto-reconnect after a prior manual disconnect (e.g. re-login).
   */
  enableReconnect() {
    this.shouldReconnect = true;
  }

  /**
   * Schedule reconnection with exponential backoff + jitter.
   * Guarantees at most one pending reconnect timer.
   */
  scheduleReconnect() {
    if (!this.shouldReconnect) return;

    // Already connected — nothing to do
    if (this.baseSocket?.connected && this.connectionState === 'CONNECTED') {
      return;
    }

    // One timer only — never stack reconnects
    if (this.reconnectTimer) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ [UnifiedSocketManager] Max reconnection attempts reached');
      this.setConnectionState('ERROR');
      return;
    }

    this.reconnectAttempts++;
    const baseDelay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );
    const jitter = Math.floor(Math.random() * Math.min(750, baseDelay * 0.25));
    const delay = baseDelay + jitter;

    this.setConnectionState('RECONNECTING');

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.shouldReconnect) return;
      if (this.baseSocket?.connected) {
        this.setConnectionState('CONNECTED');
        return;
      }
      // Clear any existing connection promise before attempting reconnect
      this.connectionPromise = null;
      // Always fully disconnect and clean up to ensure fresh connection with updated userId
      if (this.baseSocket) {
        try {
          this.baseSocket.removeAllListeners();
          this.baseSocket.disconnect();
        } catch {
          /* ignore */
        }
        this.baseSocket = null;
      }
      this.connect().catch((error) => {
        console.error('❌ [UnifiedSocketManager] Reconnection attempt failed:', error);
      });
    }, delay);
  }

  /**
   * Start heartbeat
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.lastHeartbeat = Date.now();

    if (this.baseSocket) {
      if (this._pongHandler) {
        this.baseSocket.off('pong', this._pongHandler);
      }
      this._pongHandler = () => {
        this.lastHeartbeat = Date.now();
      };
      this.baseSocket.on('pong', this._pongHandler);
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.baseSocket?.connected) {
        const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
        if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
          console.warn('⚠️ [UnifiedSocketManager] Connection appears stale. Reconnecting...');
          this.cleanupBaseSocket();
          this.baseSocket = null;
          this.connectionPromise = null;
          if (this.shouldReconnect) {
            this.scheduleReconnect();
          }
          return;
        }
        try {
          this.baseSocket.emit('ping');
        } catch {
          /* ignore */
        }
      }
    }, 10000);
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.baseSocket && this._pongHandler) {
      try {
        this.baseSocket.off('pong', this._pongHandler);
      } catch {
        /* ignore */
      }
    }
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Queue operation
   */
  queueOperation(event, data, namespace) {
    this.operationQueue.push({
      event,
      data,
      namespace,
      timestamp: Date.now(),
    });

    if (this.operationQueue.length > 100) {
      this.operationQueue.shift();
    }
  }

  /**
   * Process queued operations
   */
  processOperationQueue() {
    if (!this.baseSocket?.connected || this.operationQueue.length === 0) {
      return;
    }

    const operations = [...this.operationQueue];
    this.operationQueue = [];

    operations.forEach((op) => {
      if (Date.now() - op.timestamp < 300000) {
        if (op.namespace) {
          this.emitToNamespace(op.namespace, op.event, op.data);
        } else {
          this.baseSocket.emit(op.event, op.data);
        }
      }
    });
  }

  /**
   * Set connection state
   */
  setConnectionState(state) {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.connectionStateCallbacks.forEach((callback) => callback(state));
    }
  }

  /**
   * Get connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connectionState === 'CONNECTED' && this.baseSocket?.connected === true;
  }

  /**
   * Destroy all connections
   */
  static destroyAll() {
    if (UnifiedSocketManager.instance) {
      UnifiedSocketManager.instance.disconnect();
      UnifiedSocketManager.instance = null;
    }
  }
}

/**
 * SocketManager - Backward compatible wrapper
 * Maintains same API but uses unified connection internally.
 * Exactly one wrapper instance per namespace name.
 */
class SocketManager {
  static instances = new Map();

  /**
   * Preferred factory — always returns the singleton for this namespace.
   */
  static getOrCreate(namespace, params = { autoconnect: false }) {
    const existing = SocketManager.instances.get(namespace);
    if (existing) return existing;
    return new SocketManager(namespace, params);
  }

  constructor(namespace, params = { autoconnect: false }) {
    // Return existing instance if already created (JS `new` uses returned object)
    if (SocketManager.instances.has(namespace)) {
      const existing = SocketManager.instances.get(namespace);
      if (existing) return existing;
    }

    this.namespace = namespace;
    this.params = params;
    this.unifiedManager = UnifiedSocketManager.getInstance();
    // Re-enable reconnect after logout/destroyAll + new session
    this.unifiedManager.enableReconnect();

    SocketManager.instances.set(namespace, this);

    if (params?.autoconnect) {
      this.connect().catch(() => {});
    }
  }

  /**
   * Get socket instance for this namespace
   */
  getSocket(user) {
    // Get namespace socket from unified manager
    const namespaceSocket = this.unifiedManager.getNamespaceSocket(this.namespace, user);
    return namespaceSocket;
  }

  /**
   * Connect (connects base socket if not connected)
   */
  connect() {
    this.unifiedManager.enableReconnect();
    return this.unifiedManager.connect();
  }

  /**
   * Leave this logical namespace. Does NOT tear down the shared base connection
   * or wipe other consumers' event listeners. Use destroyAll() on logout.
   */
  disconnect() {
    this.unifiedManager.leaveNamespace(this.namespace);
  }

  /**
   * Check socket status
   */
  checkSocketStatus() {
    return this.unifiedManager.isConnected();
  }

  /**
   * Get connection state
   */
  getConnectionState() {
    return this.unifiedManager.getConnectionState();
  }

  /**
   * Destroy all connections (logout / session end)
   */
  static destroyAll() {
    UnifiedSocketManager.destroyAll();
    SocketManager.instances.clear();
  }
}

export { UnifiedSocketManager };
export default SocketManager;
